import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { LocationRow, WarehouseRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { hasPermission } from '@hq/permission';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../api/client';
import { sumWidth } from '@hq/ui-kit';
import { useAuthStore } from '../stores/auth';

const WH_KINDS = [
  { value: 'INNER_MAIN', label: '院内中心库' },
  { value: 'INNER_SUB', label: '科室二级库' },
  { value: 'OUTER_ZERO', label: '外部零库存仓' },
  { value: 'SUPPLIER', label: '供应商仓' }
];

const ZONE_TYPES = [
  { value: 'STORAGE', label: '存储区' },
  { value: 'PICK', label: '拣货区' },
  { value: 'RECEIVE', label: '收货区' },
  { value: 'RETURN', label: '退货区' },
  { value: 'SCRAP', label: '报废区' }
];

/** 仓库货位：仓库主档 + org_location 货位（入库上架/拣货定位的依据）。 */
export default function BasicWarehouse() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const profile = useAuthStore((state) => state.profile);
  const canManage = profile?.level === 'admin' || (profile?.perms ? hasPermission(profile.perms, 'sys:org:manage') : false);

  const [tab, setTab] = useState('warehouse');
  const [whEditing, setWhEditing] = useState<WarehouseRow | null>(null);
  const [whOpen, setWhOpen] = useState(false);
  const [locEditing, setLocEditing] = useState<LocationRow | null>(null);
  const [locOpen, setLocOpen] = useState(false);
  const [whFilter, setWhFilter] = useState<string>('');
  const [whForm] = Form.useForm();
  const [locForm] = Form.useForm();

  const warehouses = useQuery({ queryKey: ['basic', 'warehouse'], queryFn: () => hq.orgStructure.warehouse.list({}) });
  const depts = useQuery({ queryKey: ['basic', 'dept', 'options'], queryFn: () => hq.orgStructure.dept.list({}) });
  // 责任供应商：外仓（OUTER_ZERO / SUPPLIER）必填，决定供应商门户「可供库存」能看到哪些供货指令
  const suppliers = useQuery({
    queryKey: ['basic', 'supplier', 'options'],
    queryFn: () => hq.catalog.suppliers({ size: 200 }),
    staleTime: 5 * 60 * 1000
  });
  const whKind = Form.useWatch('whKind', whForm) as string | undefined;
  const isOuterKind = whKind === 'OUTER_ZERO' || whKind === 'SUPPLIER';
  const supplierName = (id: string) =>
    suppliers.data?.items.find((row) => row.id === id)?.supplierName ?? (id === '0' ? '-' : id);
  const locations = useQuery({
    queryKey: ['basic', 'location', whFilter],
    queryFn: () => hq.orgStructure.location.list(whFilter ? { warehouseId: whFilter } : {})
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['basic'] });
  };

  const saveWh = useMutation({
    mutationFn: () => hq.orgStructure.warehouse.save(whForm.getFieldsValue()),
    onSuccess: async () => {
      message.success('仓库已保存');
      setWhOpen(false);
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const saveLoc = useMutation({
    mutationFn: () => hq.orgStructure.location.save(locForm.getFieldsValue()),
    onSuccess: async () => {
      message.success('货位已保存');
      setLocOpen(false);
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const whColumns: ColumnsType<WarehouseRow> = [
    { title: '仓库编码', dataIndex: 'whCode', width: 130 },
    { title: '仓库名称', dataIndex: 'whName', width: 200 },
    { title: '类型', dataIndex: 'whKind', width: 130, render: (value: string) => <Tag color="blue">{WH_KINDS.find((x) => x.value === value)?.label ?? value}</Tag> },
    { title: '性质', dataIndex: 'whNature', width: 90, render: (value: string) => (value === 'VIRTUAL' ? '虚拟' : '实体') },
    { title: '定位科室', dataIndex: 'deptName', width: 150, render: (value: string | null) => value || '-' },
    {
      title: '责任供应商',
      dataIndex: 'supplierId',
      width: 180,
      render: (value: string | null, row) =>
        row.whKind === 'OUTER_ZERO' || row.whKind === 'SUPPLIER' ? supplierName(value ?? '0') : '-'
    },
    { title: '允许负库存', dataIndex: 'allowNegative', width: 110, align: 'center', render: (value: boolean) => (value ? '是' : '否') },
    { title: '状态', dataIndex: 'status', width: 90, render: (value: string) => <Tag color={value === 'ACTIVE' ? 'green' : 'default'}>{value}</Tag> },
    {
      title: '操作',
      width: 160,
      fixed: 'right',
      render: (_, row) => (
        <Space size={0}>
          <Button
            type="link"
            disabled={!canManage}
            onClick={() => {
              setWhEditing(row);
              whForm.setFieldsValue({ ...row, deptId: row.deptId === '0' ? undefined : row.deptId });
              setWhOpen(true);
            }}
          >
            编辑
          </Button>
          <Button type="link" onClick={() => { setWhFilter(row.id); setTab('location'); }}>
            货位
          </Button>
          <Button
            type="link"
            danger
            disabled={!canManage}
            onClick={() =>
              modal.confirm({
                title: `删除仓库 ${row.whName}`,
                content: '有库存或单据引用时后端会拒绝删除。',
                okButtonProps: { danger: true },
                onOk: async () => {
                  await hq.orgStructure.warehouse.remove(row.id);
                  message.success('已删除');
                  await invalidate();
                }
              })
            }
          >
            删除
          </Button>
        </Space>
      )
    }
  ];

  const locColumns: ColumnsType<LocationRow> = [
    { title: '货位编码', dataIndex: 'locationCode', width: 150 },
    { title: '仓库', dataIndex: 'warehouseName', width: 180, render: (value: string | null) => value || '-' },
    { title: '用途', dataIndex: 'zoneType', width: 110, render: (value: string) => <Tag>{ZONE_TYPES.find((x) => x.value === value)?.label ?? value}</Tag> },
    { title: 'RFID', dataIndex: 'rfidEpc', width: 180, render: (value: string | null) => value || '-' },
    { title: '排序', dataIndex: 'sortNo', width: 80, align: 'right' },
    { title: '状态', dataIndex: 'status', width: 90, render: (value: string) => <Tag color={value === 'ACTIVE' ? 'green' : 'default'}>{value}</Tag> },
    {
      title: '操作',
      width: 140,
      render: (_, row) => (
        <Space size={0}>
          <Button
            type="link"
            disabled={!canManage}
            onClick={() => {
              setLocEditing(row);
              locForm.setFieldsValue(row);
              setLocOpen(true);
            }}
          >
            编辑
          </Button>
          <Button
            type="link"
            danger
            disabled={!canManage}
            onClick={() =>
              modal.confirm({
                title: `删除货位 ${row.locationCode}`,
                content: '货位上有库存时后端会拒绝删除。',
                okButtonProps: { danger: true },
                onOk: async () => {
                  await hq.orgStructure.location.remove(row.id);
                  message.success('已删除');
                  await invalidate();
                }
              })
            }
          >
            删除
          </Button>
        </Space>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={<Space>仓库货位<Typography.Text type="secondary">仓库主档 + 货位（编辑需 sys:org:manage）</Typography.Text></Space>}
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => invalidate()} loading={warehouses.isFetching || locations.isFetching}>
            刷新
          </Button>
          {tab === 'warehouse' ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!canManage}
              onClick={() => {
                setWhEditing(null);
                whForm.resetFields();
                whForm.setFieldsValue({ whKind: 'INNER_MAIN', whNature: 'PHYSICAL', status: 'ACTIVE', allowNegative: false, isZeroStockSettle: false });
                setWhOpen(true);
              }}
            >
              新增仓库
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={!canManage || !whFilter}
              onClick={() => {
                setLocEditing(null);
                locForm.resetFields();
                locForm.setFieldsValue({ warehouseId: whFilter || undefined, zoneType: 'STORAGE', status: 'ACTIVE', sortNo: 0 });
                setLocOpen(true);
              }}
            >
              新增货位
            </Button>
          )}
        </Space>
      }
    >
      {!canManage ? <Alert type="info" showIcon style={{ marginBottom: 12 }} message="当前账号为只读：仓库与货位维护需要 sys:org:manage。" /> : null}
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'warehouse',
            label: `仓库（${warehouses.data?.length ?? 0}）`,
            children: (
              <Table<WarehouseRow>
                rowKey="id"
                size="middle"
                columns={whColumns}
                dataSource={warehouses.data ?? []}
                loading={warehouses.isPending}
                pagination={false}
                scroll={{ x: sumWidth(whColumns) }}
              />
            )
          },
          {
            key: 'location',
            label: `货位（${locations.data?.length ?? 0}）`,
            children: (
              <>
                <Space style={{ marginBottom: 12 }}>
                  <Select
                    allowClear
                    placeholder="按仓库筛选"
                    style={{ width: 260 }}
                    value={whFilter || undefined}
                    onChange={(value) => setWhFilter(value ?? '')}
                    options={(warehouses.data ?? []).map((row) => ({ value: row.id, label: row.whName }))}
                  />
                  <Typography.Text type="secondary">货位库存可在运营端「货位库存」查看</Typography.Text>
                </Space>
                <Table<LocationRow>
                  rowKey="id"
                  size="middle"
                  columns={locColumns}
                  dataSource={locations.data ?? []}
                  loading={locations.isPending}
                  pagination={{ pageSize: 20, hideOnSinglePage: true }}
                  scroll={{ x: sumWidth(locColumns) }}
                />
              </>
            )
          }
        ]}
      />

      <Modal
        title={whEditing ? '编辑仓库' : '新增仓库'}
        open={whOpen}
        onCancel={() => setWhOpen(false)}
        onOk={() => whForm.submit()}
        confirmLoading={saveWh.isPending}
        destroyOnHidden
        width={640}
      >
        <Form form={whForm} layout="vertical" onFinish={() => saveWh.mutate()} preserve={false}>
          <Form.Item name="id" hidden><Input /></Form.Item>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="whCode" label="仓库编码" rules={[{ required: true }]} style={{ width: 220 }}>
              <Input maxLength={32} />
            </Form.Item>
            <Form.Item name="whName" label="仓库名称" rules={[{ required: true }]} style={{ width: 300 }}>
              <Input maxLength={128} />
            </Form.Item>
          </Space>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="whKind" label="仓库类型" style={{ width: 220 }}>
              <Select
                options={WH_KINDS}
                onChange={(value: string) => {
                  // 切回院内库时清掉责任供应商，避免把外仓归属带到院内仓上
                  if (value !== 'OUTER_ZERO' && value !== 'SUPPLIER') whForm.setFieldValue('supplierId', undefined);
                }}
              />
            </Form.Item>
            <Form.Item name="deptId" label="挂载科室（二级库）" style={{ width: 300 }}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={(depts.data ?? []).map((row) => ({ value: row.id, label: row.deptName }))}
                placeholder="二级库需挂载科室"
              />
            </Form.Item>
          </Space>
          {isOuterKind ? (
            <Form.Item
              name="supplierId"
              label="责任供应商"
              rules={[{ required: true, message: '外部零库存仓 / 供应商仓必须绑定责任供应商' }]}
              extra="供应商门户的「可供库存」只显示派给该供应商的外仓供货指令"
              style={{ width: 380 }}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                loading={suppliers.isPending}
                placeholder="选择承担该仓履约的供应商"
                options={(suppliers.data?.items ?? []).map((row) => ({
                  value: row.id,
                  label: `${row.supplierCode} ${row.supplierName}`
                }))}
              />
            </Form.Item>
          ) : null}
          <Form.Item name="address" label="地址">
            <Input maxLength={255} />
          </Form.Item>
          <Space size={24}>
            <Form.Item name="allowNegative" label="允许负库存" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="isZeroStockSettle" label="零库存结算" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="status" label="状态"><Select style={{ width: 120 }} options={[{ value: 'ACTIVE', label: '启用' }, { value: 'LOCKED', label: '锁定' }]} /></Form.Item>
          </Space>
        </Form>
      </Modal>

      <Modal
        title={locEditing ? '编辑货位' : '新增货位'}
        open={locOpen}
        onCancel={() => setLocOpen(false)}
        onOk={() => locForm.submit()}
        confirmLoading={saveLoc.isPending}
        destroyOnHidden
      >
        <Form form={locForm} layout="vertical" onFinish={() => saveLoc.mutate()} preserve={false}>
          <Form.Item name="id" hidden><Input /></Form.Item>
          <Form.Item name="warehouseId" label="所属仓库" rules={[{ required: true }]}>
            <Select options={(warehouses.data ?? []).map((row) => ({ value: row.id, label: row.whName }))} />
          </Form.Item>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="locationCode" label="货位编码" rules={[{ required: true }]} style={{ width: 220 }}>
              <Input maxLength={32} />
            </Form.Item>
            <Form.Item name="zoneType" label="用途" style={{ width: 200 }}>
              <Select options={ZONE_TYPES} />
            </Form.Item>
            <Form.Item name="sortNo" label="排序" style={{ width: 110 }}>
              <InputNumber min={0} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Form.Item name="rfidEpc" label="RFID 标签">
            <Input maxLength={64} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select style={{ width: 140 }} options={[{ value: 'ACTIVE', label: '启用' }, { value: 'INACTIVE', label: '停用' }]} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
